import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CalendarStar } from "@phosphor-icons/react";
import { api } from "@/lib/api";

/**
 * Iter 85.38 · Inline Activity association editor for an ordinary
 * Meeting Note viewed in `NoteViewerDialog`.
 *
 * Rendered ONLY when the note is a plain meeting-type note (never on
 * activity briefings, morning briefs, or memos). The editor loads the
 * activity briefings scoped to the note's (client_id, engagement_id)
 * pair and PATCHes `activity_briefing_id` on the note when the partner
 * picks / clears an option.
 *
 * Empty / partial-scope UX
 * ────────────────────────
 * - Missing client_id + engagement_id  → shows a hint prompting the
 *   partner to set both before an activity can be linked.
 * - Client + engagement set but no activities exist under that scope
 *   → shows a small "No activities yet" caption so the partner still
 *   sees the mechanism.
 * - Otherwise renders a live Select with "No activity" + every valid
 *   activity briefing.
 *
 * Never fetches on activity briefings themselves — those ARE activities
 * and self-association is invalid at the backend.
 */
export default function MeetingNoteActivityEditor({ note, onNoteChange }) {
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const canLoad = note && note.note_type === "meeting" && note.client_id && note.engagement_id;

  useEffect(() => {
    if (!canLoad) { setOptions([]); return; }
    let alive = true;
    setLoading(true);
    api
      .get("/notes", {
        params: {
          client_id: note.client_id,
          engagement_id: note.engagement_id,
          note_type: "activity_briefing",
        },
      })
      .then((r) => { if (alive) setOptions(r.data || []); })
      .catch(() => { if (alive) setOptions([]); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [canLoad, note?.client_id, note?.engagement_id]);

  // Never surface on non-meeting notes — activity briefings themselves,
  // memos, morning briefs, insights, call logs stay untouched.
  if (!note || note.note_type !== "meeting") return null;

  const currentValue = note.activity_briefing_id || "none";
  const applyPatch = async (nextValue) => {
    const patch = { activity_briefing_id: nextValue === "none" ? null : nextValue };
    setSaving(true);
    try {
      const { data } = await api.patch(`/notes/${note.id}`, patch);
      onNoteChange?.(data);
      if (patch.activity_briefing_id) toast.success("Activity linked");
      else toast.success("Activity link removed");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update activity link");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="border-b border-border bg-secondary/20 px-6 py-3"
      data-testid="meeting-note-activity-editor"
    >
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-1.5 text-[0.625rem] font-mono uppercase tracking-widest text-brass shrink-0">
          <CalendarStar size={12} weight="fill" /> Activity
        </div>
        {(!note.client_id || !note.engagement_id) ? (
          <span className="text-xs text-muted-foreground italic">
            Set a Client and Engagement on this note to link it to an activity.
          </span>
        ) : loading ? (
          <span className="text-xs text-muted-foreground">Loading activities…</span>
        ) : options.length === 0 ? (
          <span className="text-xs text-muted-foreground italic">
            No activities under this Client / Engagement yet. Create an Activity briefing from the Advisory Desk to link it here.
          </span>
        ) : (
          <div className="flex-1 min-w-[240px] max-w-md">
            <Select
              value={currentValue}
              onValueChange={applyPatch}
              disabled={saving}
            >
              <SelectTrigger className="rounded-none bg-secondary/40 h-8" data-testid="meeting-note-activity-select">
                <SelectValue placeholder="No activity" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">No activity — standalone meeting note</SelectItem>
                {options.map((a) => {
                  const when = a.activity_date ? ` · ${a.activity_date}` : "";
                  return <SelectItem key={a.id} value={a.id}>{a.title}{when}</SelectItem>;
                })}
              </SelectContent>
            </Select>
          </div>
        )}
      </div>
    </div>
  );
}
